// vmAuthenticate.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sendRequest } from './vmUtils';
import { lambdaURL } from '../config';
import { firebaseService } from '../firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { VMAuthStorage } from './vmAuthStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Separate loading states for different phases
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  const [systemLoading, setSystemLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Combined loading state for components
  const isLoading = firebaseLoading || systemLoading;

  // Centralized user initialization
  const initializeUser = useCallback(async (firebaseUser) => {
    console.log("Initializing user with firebaseUser:", firebaseUser);
    const pendingAuthUrl = VMAuthStorage.getPendingAuthUrl();
    const storedEmail = VMAuthStorage.getSignInEmail();

    // If we have pending auth data but no firebase user, handle the email link sign-in
    if (pendingAuthUrl && storedEmail && !firebaseUser) {
      try {
        // Don't clear the auth data yet - we'll do that after successful sign-in
        const result = await firebaseService.completeSignInWithLink(storedEmail, pendingAuthUrl);

        if (result.success) {
          // The successful sign-in will trigger another auth state change
          // Don't clear the auth data here - let the next auth state change handle it
          return;
        } else {
          console.error('Email link sign-in failed:', result.error);
          VMAuthStorage.clearAllAuthData();
        }
      } catch (error) {
        console.error('Error completing email link sign-in:', error);
        VMAuthStorage.clearAllAuthData();
      }
      return;
    }

    if (!firebaseUser) {
      console.log("No firebase user found");
      setUser(null);
      setSystemLoading(false);
      return;
    }

    try {
      const token = await firebaseUser.getIdToken();
      console.log("Firebase token obtained:", !!token);
      VMAuthStorage.setAuthData(token, firebaseUser.uid);

      // Get or create user in your system
      const getUserResponse = await sendRequest(lambdaURL, { command: "getUser" });
      console.log("GetUserResponse: ", getUserResponse);

      let userData;
      if (!getUserResponse.body || getUserResponse.body === "null") {
        console.log("No user found, attempting to create user");
        const createUserResponse = await sendRequest(lambdaURL, {
          command: "createUser",
          email: firebaseUser.email.toLowerCase(),
          firebaseUid: firebaseUser.uid
        });

        console.log("CreateUserResponse:", createUserResponse);
        if (!createUserResponse.body) {
          throw new Error('Failed to create user in system');
        }
        userData = JSON.parse(createUserResponse.body);
      } else {
        userData = JSON.parse(getUserResponse.body);
      }

      console.log("Final userData:", userData);
      const userState = {
        userId: userData.uuid,
        isAuthenticated: true,
        isAdmin: userData.isAdmin
      };

      setUser(userState);
      VMAuthStorage.setSystemUserId(userData.uuid);

      // Only clear pending auth data after successful initialization
      if (pendingAuthUrl) {
        VMAuthStorage.clearSignInData();
      }
    } catch (error) {
      console.error('Error initializing user:', error);
      setUser(null);
      VMAuthStorage.clearAllAuthData();
    } finally {
      setSystemLoading(false);
    }
  }, []);

  //Check for persisted authentication
  useEffect(() => {
    const checkPersistedAuth = async () => {
      const auth = getAuth();

      // Check local storage
      const storedToken = VMAuthStorage.getAuthToken();
      const storedFirebaseUid = VMAuthStorage.getFirebaseUserId();

      console.log("Stored authentication details:", {
        tokenPresent: !!storedToken,
        firebaseUidPresent: !!storedFirebaseUid
      });

      VMAuthStorage.logAuthState();

      // If we have a stored token but no current user, you might want to handle this
      if (storedToken && storedFirebaseUid && !auth.currentUser) {
        console.warn("Stored token exists, but no current Firebase user");
        // Potentially trigger a re-authentication or token refresh
      }
    };

    checkPersistedAuth();
  }, []);

  // Firebase auth state listener
  useEffect(() => {
    const auth = getAuth();
    //console.log("Initial auth object:", auth);
    //if (auth.currentUser) {
    //  console.log("Current user on initial load:", auth.currentUser);
    //} else {
    //  console.log("No current user on initial load");
    //}

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      //console.log("Auth state changed. Received firebaseUser:", firebaseUser);
      setFirebaseLoading(true);
      try {
        await initializeUser(firebaseUser);
      } catch (error) {
        console.error("Error in onAuthStateChanged:", error);
        setUser(null);
        VMAuthStorage.clearAllAuthData();
      } finally {
        setFirebaseLoading(false);
      }
    },
    (error) => {
      console.error("Auth state change error:", error);
      setFirebaseLoading(false);
    }
  );

    return () => unsubscribe();
  }, [initializeUser]);

  // Auth methods
  const login = async (email, password) => {
    try {
    const result = await firebaseService.signIn(email, password);
      if (!result.success) {
        throw new Error(result.error);
      }
      return { success: true, userId: result.userId };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password) => {
    try {
    const result = await firebaseService.register(email, password);
      if (!result.success) {
        throw new Error(result.error);
      }
      return { success: true, userId: result.userId };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await firebaseService.signOut();
      setUser(null);
      VMAuthStorage.clearAllAuthData();
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Provide loading state and authenticated status alongside user data
  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// SignIn component
export const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, register } = useAuth();

  const handleSignIn = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
  };

  const handleCreateAccount = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    const result = await register(email, password);
    if (!result.success) {
      setError(result.error);
    }
  };

  return (
    <div className="vm-section">
      <div className="vm-section-title">
        <h2>Welcome to Vote Machine</h2>
      </div>

      <div className="vm-quoted-text">
        "A well-instructed people alone can be permanently a free people."
        - James Madison
      </div>

      <div className="vm-form-vertical">
        <input
          type="email"
          className="vm-form-input"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          type="password"
          className="vm-form-input"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && <div className="vm-form-error">{error}</div>}

        <div className="vm-proposal-vote-button-container">

          <button
            className="vm-form-button"
            onClick={handleCreateAccount}
          >
            Sign Up
          </button>

          <button
            className="vm-form-button"
            onClick={handleSignIn}
          >
            Sign In
          </button>

        </div>
      </div>
    </div>
  );
};